import { useGetIapUserQuery } from '@/services/customizationApi';
import { resolvePlayerName } from '@/lib/playerName';
import { cn } from '@/lib/cn';
import { useLinguaFormato } from '@/hooks/useLinguaFormato';
import type { Participant } from '@/types/tournament';

interface MatchUserBannerProps {
  participant: Participant | null | undefined;
  /** Brand colour when the participant has checked in, otherwise the
   *  status flag colour from MatchDetail. */
  borderColor: string;
  /** Optional label on the right side (e.g. "YOU"). */
  badge?: string;
  /** "Waiting for opponent" placeholder when participant is null. */
  waitingLabel: string;
}

/**
 * Compact participant banner for the autohandled-match screen.
 * Mirrors mobile-app/src/components/tournaments/user-banner.tsx —
 * left avatar circle, nickname, optional badge, configurable border tint.
 *
 * The player's selected IAP banner (resolved by Laravel user id via the
 * customization service) is rendered as the backdrop when present, with a
 * dark scrim for text legibility; otherwise it falls back to the QLASH dark
 * surface. Name resolution goes through `resolvePlayerName` so the literal
 * "unknown" the Laravel API sometimes returns no longer leaks to the UI.
 */
export function MatchUserBanner({
  participant,
  borderColor,
  badge,
  waitingLabel,
}: MatchUserBannerProps) {
  const lingua = useLinguaFormato();
  const userId = participant?.profile?.user?.id;
  const { data: iap } = useGetIapUserQuery(userId as number, { skip: userId == null });
  const bannerUrl = iap?.banner?.media_uri ?? null;

  const nickname = resolvePlayerName(participant);
  const avatar = participant?.profile?.user?.avatar ?? null;
  const initial = (nickname ?? '?').charAt(0).toLocaleUpperCase(lingua);

  return (
    <div
      className="relative mx-4 my-2 overflow-hidden rounded-2xl bg-bg-secondary"
      style={{ border: `2px solid ${borderColor}` }}
    >
      {bannerUrl && (
        <>
          <div
            aria-hidden
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: `url(${bannerUrl})` }}
          />
          <div aria-hidden className="absolute inset-0 bg-black/50" />
        </>
      )}

      <div className="relative flex items-center gap-3 p-3">
        <div className="relative h-12 w-12 shrink-0 overflow-hidden rounded-full bg-bg-tertiary">
          {avatar ? (
            <img
              src={avatar}
              alt=""
              className="h-full w-full object-cover"
              loading="lazy"
            />
          ) : (
            <div className="flex h-full w-full items-center justify-center text-base font-semibold text-fg">
              {initial}
            </div>
          )}
        </div>

        <div className="min-w-0 flex-1">
          <p
            className={cn(
              'truncate text-sm font-semibold',
              bannerUrl ? 'text-white' : nickname ? 'text-fg' : 'text-fg-muted italic',
            )}
          >
            {nickname ?? waitingLabel}
          </p>
        </div>

        {badge && (
          <span className="rounded-full bg-brand/15 px-2 py-0.5 text-[10px] font-bold uppercase text-brand">
            {badge}
          </span>
        )}
      </div>
    </div>
  );
}
